import { useCallback, useEffect, useMemo, useState } from "react";
import webinarsMock from "../mocks/webinars";
import { AUTH_USER_NAME_KEY } from "../services/auth.service";

const STORAGE_KEYS = {
  data: "webinars_data",
  inscripciones: "webinars_inscripciones",
  usuario: "webinars_usuario",
};

const getStoredUserName = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(AUTH_USER_NAME_KEY) || "";
};

const DEFAULT_USUARIO = {
  nombre: getStoredUserName() || "María Pérez",
  correo: "maria.perez@almacenes.com",
};

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const loadFromStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(key);
  return stored ? safeParse(stored, fallback) : fallback;
};

const saveToStorage = (key, value) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

const mergeWebinarsWithMock = (storedWebinars) => {
  if (!Array.isArray(storedWebinars) || storedWebinars.length === 0) {
    return webinarsMock;
  }

  return storedWebinars.map((storedWebinar) => {
    const mockWebinar = webinarsMock.find((webinar) => webinar.id === storedWebinar.id);
    return mockWebinar ? { ...mockWebinar, ...storedWebinar } : storedWebinar;
  });
};

export const clearWebinarsStorage = () => {
  if (typeof window === "undefined") return;
  Object.values(STORAGE_KEYS).forEach((key) => window.localStorage.removeItem(key));
};

export const useWebinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [usuario, setUsuario] = useState(DEFAULT_USUARIO);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const storedWebinars = loadFromStorage(STORAGE_KEYS.data, null);
    const storedInscripciones = loadFromStorage(STORAGE_KEYS.inscripciones, []);
    const storedUsuario = loadFromStorage(STORAGE_KEYS.usuario, null);

    const realName = getStoredUserName();
    const initialUsuario = storedUsuario && typeof storedUsuario === "object"
      ? {
          nombre: realName || storedUsuario.nombre || DEFAULT_USUARIO.nombre,
          correo: storedUsuario.correo || DEFAULT_USUARIO.correo,
        }
      : { ...DEFAULT_USUARIO, nombre: realName || DEFAULT_USUARIO.nombre };

    if (storedWebinars && Array.isArray(storedWebinars) && storedWebinars.length > 0) {
      setWebinars(mergeWebinarsWithMock(storedWebinars));
    } else {
      setWebinars(webinarsMock);
      saveToStorage(STORAGE_KEYS.data, webinarsMock);
    }

    setInscripciones(Array.isArray(storedInscripciones) ? storedInscripciones : []);
    setUsuario(initialUsuario);
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (isReady) {
      saveToStorage(STORAGE_KEYS.data, webinars);
    }
  }, [webinars, isReady]);

  useEffect(() => {
    if (isReady) {
      saveToStorage(STORAGE_KEYS.inscripciones, inscripciones);
    }
  }, [inscripciones, isReady]);

  useEffect(() => {
    if (isReady) {
      saveToStorage(STORAGE_KEYS.usuario, usuario);
    }
  }, [usuario, isReady]);

  const eventosPorFecha = useMemo(
    () =>
      webinars.reduce((acc, evento) => {
        if (!evento.fecha) return acc;
        const dateKey = evento.fecha;
        acc[dateKey] = acc[dateKey] ? [...acc[dateKey], evento] : [evento];
        return acc;
      }, {}),
    [webinars]
  );

  const isInscrito = useCallback(
    (id) => inscripciones.includes(id),
    [inscripciones]
  );

  const inscribir = useCallback((id) => {
    setInscripciones((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const cancelarInscripcion = useCallback((id) => {
    setInscripciones((prev) => prev.filter((item) => item !== id));
  }, []);

  const getEventoById = useCallback(
    (id) => webinars.find((evento) => evento.id === id),
    [webinars]
  );

  return {
    webinars,
    eventosPorFecha,
    inscripciones,
    usuario,
    setUsuario,
    isInscrito,
    inscribir,
    cancelarInscripcion,
    getEventoById,
    isReady,
  };
};
